import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Textarea } from '../ui/Textarea';
import { Button } from '../ui/Button';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import VoiceConversation, { canConverse } from './VoiceConversation';
import WalkieTalkie, { getSendWord } from './WalkieTalkie';
import { localToday } from '../../utils/date';

// The three ways to log. `type` is the inline composer; `auto` and `walkie` are hands-free
// full-screen surfaces. The pick is remembered (localStorage) so it sticks between visits.
// `short` is the pill label — kept to one word so the toggle never wraps to two lines on a
// phone; `label` is the fuller name used in the launch button ("Start walkie-talkie").
const LOG_MODES = [
  { key: 'type', label: 'Type', short: 'Type' },
  { key: 'auto', label: 'Auto', short: 'Auto', voice: true },
  { key: 'walkie', label: 'Walkie-talkie', short: 'Walkie', voice: true },
];
const MODE_KEY = 'vg_log_mode';
const readMode = () => {
  try {
    const v = localStorage.getItem(MODE_KEY);
    return LOG_MODES.some((m) => m.key === v) ? v : 'type';
  } catch (e) { return 'type'; }
};

const CATEGORY_EMOJI = {
  exercise: '🏃',
  nutrition: '🍽️',
  sleep: '😴',
  biometric: '📏',
  mental_health: '🧠',
  therapy: '🌿',
  medical: '🩺',
  environmental: '🌤️',
  social: '👥',
  other: '•',
};

const entryLabel = (e) => {
  const emoji = CATEGORY_EMOJI[(e.category_slug || '').split('.')[0]] || '•';
  const name = (e.entry_type || '').replace(/_/g, ' ');
  let detail = '';
  if (e.value != null) detail = ` ${e.value}${e.value_unit ? ' ' + e.value_unit : ''}`;
  else if (e.calories != null) detail = ` ${Math.round(e.calories)} cal`;
  else if (e.duration_minutes != null) detail = ` ${e.duration_minutes} min`;
  return `${emoji} ${name}${detail}`;
};

// Prefer a container the transcription API takes as-is. Safari/iOS only offers mp4;
// leaving the type empty lets that browser pick its own default rather than failing.
const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
};

const formatElapsed = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// Unlock mobile speech synthesis inside a tap gesture, so the first spoken reply
// isn't silently dropped by the browser's autoplay policy. Exported because the Log
// page's capture bar starts voice sessions from outside this component, and the
// unlock has to happen in THAT tap. Harmless anywhere else.
export const warmSpeech = () => {
  try {
    const synth = window.speechSynthesis;
    if (synth) {
      synth.cancel();
      synth.resume();
      const warm = new SpeechSynthesisUtterance(' ');
      warm.volume = 0;
      synth.speak(warm);
    }
  } catch (e) { /* noop */ }
};

// Prod's nginx rejects bodies over 1MB and raising it needs hands-on sudo. Capping the
// recording bounds the upload without depending on the encoder honoring a bitrate.
// Measured: Chrome defaults to 128kbps opus (~14.5KB/s), so 60s lands near 870KB —
// under the cap with room to spare. Don't raise this without re-measuring.
const MAX_RECORDING_SECONDS = 60;
// Belt and braces: if an encoder is fatter than measured, say so plainly rather than
// letting the request bounce off the proxy as opaque HTML.
const MAX_UPLOAD_BYTES = 1000 * 1024;

// `fill` makes the card stretch to fill a flex parent (the app-like Log view) so the chat
// grows to the whole window and the composer + mode bar dock at the bottom. Without it the
// card keeps its old fixed height for use inside a normal scrolling page.
// `startIn` drops the card straight into a mode on mount — 'type' focuses the composer,
// 'auto'/'walkie' open the live session immediately. The Log page's capture bar uses it so
// the button you tapped IS the session you get, with no second Start tap. It deliberately
// does not touch the remembered mode: an explicit toggle is a preference, a bar tap isn't.
const ConversationLogger = ({ onEntrySaved, defaultDate, fill, startIn }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  // The date is owned by the page now (the title-row picker); we just follow it.
  const date = defaultDate || localToday();
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  // Which of the three input styles is selected, and whether a hands-free full-screen
  // surface is currently open. `mode` is remembered; `overlay` is the live session.
  const [mode, setMode] = useState(() =>
    LOG_MODES.some((m) => m.key === startIn) ? startIn : readMode());
  const [overlay, setOverlay] = useState(
    startIn === 'auto' || startIn === 'walkie' ? startIn : null,
  );
  const [showInfo, setShowInfo] = useState(false);
  // Shown only when a recording ends without producing text, so a silent failure on a
  // phone (where there's no console to check) still says what actually happened.
  const [diag, setDiag] = useState('');
  const scrollRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const cancelledRef = useRef(false);
  // Lets the elapsed-timer effect trigger the auto-stop without depending on a
  // callback defined further down.
  const stopRecordingRef = useRef(null);

  // Recording needs MediaRecorder + a secure context (HTTPS or localhost). On the LAN
  // HTTP dev box getUserMedia is absent, so the mic button correctly hides itself there.
  const canRecord = typeof window !== 'undefined'
    && typeof MediaRecorder !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const releaseMic = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Never leave the mic hot if the user navigates away mid-recording.
  useEffect(() => () => {
    cancelledRef.current = true;
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch (e) { /* already stopped */ }
    }
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  // Elapsed counter — the honest "still recording" signal that the old pulsing
  // button couldn't give, since a visibly climbing timer can't be faked by a pause.
  useEffect(() => {
    if (!recording) return undefined;
    setElapsed(0);
    const id = setInterval(() => setElapsed((s) => {
      const next = s + 1;
      if (next >= MAX_RECORDING_SECONDS) {
        // Stop ourselves rather than let the upload bounce off the proxy.
        toast('That’s a long one — wrapping it up so it sends cleanly.');
        stopRecordingRef.current?.();
      }
      return next;
    }), 1000);
    return () => clearInterval(id);
  }, [recording]);

  const sendForTranscription = useCallback(async (blob, mimeType) => {
    setTranscribing(true);
    try {
      const form = new FormData();
      const ext = mimeType.includes('mp4') ? 'mp4' : mimeType.includes('ogg') ? 'ogg' : 'webm';
      form.append('audio', blob, `clip.${ext}`);
      // apiClient defaults every request to Content-Type: application/json. For a
      // FormData upload that header must be removed so the browser can set
      // multipart/form-data WITH its boundary — otherwise the server sees a JSON
      // content-type on a multipart body, can't find the field, and returns 422.
      const res = await apiClient.post('/api/health/transcribe', form, {
        timeout: 120000,
        headers: { 'Content-Type': undefined },
      });
      const transcript = (res.data?.text || '').trim();
      if (!transcript) {
        // Audio arrived but held no speech. Note the size so "sent nothing" and
        // "sent audio nobody could hear" stay distinguishable.
        setDiag(`Sent ${Math.round(blob.size / 1024)}KB but no words were recognized.`);
        toast('I didn’t catch any words there — try again a bit closer to the mic.');
        return;
      }
      // Append rather than replace, so dictating twice builds one message and never
      // silently eats what the user already typed.
      setText((prev) => (prev.trim() ? `${prev.trim()} ${transcript}` : transcript));
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      setDiag(`Upload failed (${status || 'no response'}) after ${Math.round(blob.size / 1024)}KB.`);
      if (status === 429) {
        toast.error(detail || 'Slow down a moment — too many recordings.');
      } else if (status === 413) {
        // Can come from nginx as HTML rather than our JSON, so don't trust `detail`.
        toast.error('That recording was too long. Try a shorter one, or type it.');
      } else {
        toast.error(detail || 'Couldn’t transcribe that. Please try again or type it.');
      }
    } finally {
      setTranscribing(false);
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (!canRecord || recording || transcribing) return;
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      if (e.name === 'NotAllowedError' || e.name === 'SecurityError') {
        toast.error('Microphone access denied. Please allow microphone permissions.');
      } else {
        toast.error('Couldn’t reach your microphone. You can type instead.');
      }
      return;
    }

    cancelledRef.current = false;
    streamRef.current = stream;
    chunksRef.current = [];
    setDiag('');

    const mimeType = pickMimeType();
    let recorder;
    try {
      // Deliberately no audioBitsPerSecond: forcing a bitrate is encoder-specific and
      // a mobile encoder that dislikes the value can run happily while producing no
      // data at all. The 1MB proxy cap is enforced by the duration limit below
      // instead, which doesn't depend on the encoder honoring anything.
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch (e) {
      releaseMic();
      toast.error('Recording isn’t supported in this browser. Please type instead.');
      return;
    }

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
    };

    recorder.onerror = () => {
      releaseMic();
      setRecording(false);
      toast.error('Recording stopped unexpectedly. Please try again.');
    };

    recorder.onstop = () => {
      releaseMic();
      setRecording(false);
      const type = recorder.mimeType || mimeType || 'audio/webm';
      const chunkCount = chunksRef.current.length;
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      // Unmount or an explicit discard — don't spend a transcription on it. This used
      // to return silently, which made a failed recording indistinguishable from a
      // broken one; now every path that ends without text says why.
      if (cancelledRef.current) {
        setDiag(`Recording was discarded before it could be sent (${chunkCount} chunks).`);
        return;
      }
      if (blob.size < 1024) {
        // The mic ran but produced no audio — an encoder/permission problem, not the
        // user talking too briefly. Report what we actually got so it's diagnosable.
        setDiag(`No audio captured — ${chunkCount} chunks, ${blob.size} bytes, ${type}.`);
        toast.error('That recording came through empty. Please try again, or type it.');
        return;
      }
      if (blob.size > MAX_UPLOAD_BYTES) {
        setDiag(`That clip is ${Math.round(blob.size / 1024)}KB — too big to upload. Try a shorter one.`);
        toast.error('That recording was too long to send. Please try a shorter one.');
        return;
      }
      setDiag('');
      sendForTranscription(blob, type);
    };

    recorderRef.current = recorder;
    // Timeslice: force a data callback every second instead of relying solely on the
    // final flush at stop(). Mobile encoders are less reliable about that last flush,
    // and this way a recording that starts producing audio can't be lost wholesale.
    recorder.start(1000);
    setRecording(true);
  }, [canRecord, recording, transcribing, releaseMic, sendForTranscription]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      // Flush whatever the encoder is holding before stopping, so the final second
      // of speech can't be dropped along with the last partial chunk.
      try { recorderRef.current.requestData(); } catch (e) { /* not all engines allow it */ }
      recorderRef.current.stop();
    }
  }, []);
  stopRecordingRef.current = stopRecording;

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    if (recording) stopRecording();

    // History = the conversation so far, last 12 turns.
    const history = messages
      .slice(-12)
      .map((m) => ({ role: m.role, content: m.text }));

    setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
    setText('');
    setSending(true);

    try {
      const res = await apiClient.post('/api/health/conversation', {
        message: trimmed,
        history,
        date,
      }, { timeout: 180000 });
      const { reply, entries, logged_count } = res.data;
      setMessages((prev) => [...prev, { role: 'assistant', text: reply, entries: entries || [] }]);
      if (logged_count > 0 && onEntrySaved) onEntrySaved();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 429) {
        toast.error(detail || 'Slow down a moment — too many messages.');
      } else if (error.response?.status === 401) {
        toast.error('Please log in again to keep logging.');
      } else {
        toast.error(detail || 'Something went wrong. Please try again.');
      }
      // Surface the failure inline so the chat doesn't look stuck.
      setMessages((prev) => [...prev, {
        role: 'assistant',
        text: "Sorry — I couldn't log that just now. Mind trying again?",
      }]);
    } finally {
      setSending(false);
    }
  };

  // Pick a mode: this only SETS the mode (remembered). It does NOT enter a session — the
  // Start button below the toggle does that. Toggling should never drop you straight into a
  // live mic.
  const selectMode = (key) => {
    setMode(key);
    setShowInfo(false);
    try { localStorage.setItem(MODE_KEY, key); } catch (e) { /* noop */ }
    setOverlay(null);
  };

  const startSession = () => {
    warmSpeech();
    setOverlay(mode);
  };

  const activeMode = LOG_MODES.find((m) => m.key === mode) || LOG_MODES[0];

  return (
    <div className={`card-glow rounded-xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card overflow-hidden ${
      fill ? 'flex flex-1 min-h-0 flex-col' : ''
    }`}>
      {/* Body — voice sessions now run INLINE here (not a full-screen takeover), so the
          checklist above stays visible and ticks green as you log. In `fill` mode the body
          grows to the whole window; otherwise all three modes share one fixed height so
          switching Type/Auto/Walkie never resizes the card, and a live session stays
          compact (Auto is a small status strip; Walkie needs a touch more room). */}
      <div className={`flex flex-col min-h-0 ${
        fill ? 'flex-1' : (overlay === 'walkie' ? 'h-[17rem]' : overlay === 'auto' ? 'h-[13rem]' : 'h-[212px]')
      }`}>
        {overlay === 'auto' ? (
          <VoiceConversation
            onClose={() => setOverlay(null)}
            onEntrySaved={onEntrySaved}
            defaultDate={date}
          />
        ) : overlay === 'walkie' ? (
          <WalkieTalkie
            onClose={() => setOverlay(null)}
            onEntrySaved={onEntrySaved}
            defaultDate={date}
          />
        ) : mode === 'type' ? (
          <>
            {/* Conversation — grows up from the composer. Empty to start; a faint line
                names the pane rather than repeating the composer's placeholder. */}
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
              {messages.length === 0 && !sending && (
                <div className="flex h-full items-center justify-center px-4 text-center">
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Type or talk below — your logged items land here.
                  </p>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-primary-500 text-[#1a1200] rounded-br-sm'
                        : 'bg-gray-100 dark:bg-surface-dark-raised text-gray-900 dark:text-gray-100 rounded-bl-sm'
                    }`}
                  >
                    {m.text}
                    {m.entries && m.entries.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {m.entries.map((e, j) => (
                          <span
                            key={j}
                            className="inline-flex items-center rounded-full bg-white/70 dark:bg-gray-900/50 border border-primary-200 dark:border-primary-800 px-2 py-0.5 text-sm font-medium text-primary-800 dark:text-primary-200"
                          >
                            {entryLabel(e)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {sending && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 dark:bg-surface-dark-raised rounded-2xl rounded-bl-sm px-4 py-3">
                    <span className="flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Composer — one row: the box plus a single button that is the mic when the
                box is empty and the send arrow once there's something to send. One thing
                to press, so it never reads as "which button do I want?". */}
            <div className="border-t border-surface-border dark:border-surface-dark-border p-3">
              <div className="flex items-end gap-2">
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="What did you eat, or do?"
                  maxLength={1000}
                  rows={1}
                  autoFocus={startIn === 'type'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  className="flex-1 resize-none !min-h-[2.75rem] max-h-28 py-2.5 leading-snug"
                />
                {recording ? (
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    onClick={stopRecording}
                    title="Stop recording"
                    className="h-11 w-11 shrink-0 ring-2 ring-red-500"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 7.5A2.25 2.25 0 0 1 7.5 5.25h9a2.25 2.25 0 0 1 2.25 2.25v9a2.25 2.25 0 0 1-2.25 2.25h-9a2.25 2.25 0 0 1-2.25-2.25v-9Z" />
                    </svg>
                  </Button>
                ) : text.trim() || !canRecord ? (
                  <Button
                    type="button"
                    size="icon"
                    onClick={handleSend}
                    disabled={sending || !text.trim()}
                    className="h-11 w-11 shrink-0"
                    title="Send"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path d="M3.478 2.404a.75.75 0 0 0-.926.941l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.404Z" />
                    </svg>
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={startRecording}
                    disabled={transcribing}
                    title="Record a voice note"
                    className="h-11 w-11 shrink-0"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z" />
                    </svg>
                  </Button>
                )}
              </div>
              {/* Status line only appears when there's something to say — recording,
                  transcribing, or an error. No idle character-count clutter. */}
              {recording ? (
                <span className="mt-1.5 flex items-center gap-2 text-sm font-medium text-red-600 dark:text-red-400">
                  <span className="relative flex h-2.5 w-2.5 shrink-0">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-600" />
                  </span>
                  Recording {formatElapsed(elapsed)} — tap the square when you're done.
                </span>
              ) : transcribing ? (
                <span className="mt-1.5 block text-sm font-medium text-primary-700 dark:text-primary-300">
                  Writing down what you said…
                </span>
              ) : diag ? (
                <span className="mt-1.5 block text-sm font-medium text-red-600 dark:text-red-400">
                  {diag}
                </span>
              ) : null}
            </div>
          </>
        ) : (
          /* Voice modes idle: the card shows a centered launcher in the same fixed body so
             the toggle never changes the card's size. Tapping Start swaps this for the live
             session inline (above), which grows the card — the checklist stays put. */
          <div className="flex-1 px-6 flex flex-col items-center justify-center text-center gap-3">
            <p className="text-base font-medium text-gray-800 dark:text-gray-100">
              {mode === 'walkie'
                ? <>Talk as long as you like, then say <span className="font-semibold">“{getSendWord()}”</span> or tap Send.</>
                : 'Just start talking; it replies when you pause.'}
            </p>
            <Button
              type="button"
              onClick={startSession}
              className="h-11 px-6 text-base"
            >
              Start {activeMode.label.toLowerCase()}
            </Button>
          </div>
        )}
      </div>

      {/* Mode toggle — pinned to the bottom so it's the same control in every mode.
          Compact centered pill; the info popover opens upward so it can't run off the
          bottom of a phone screen. Voice modes hide where the mic can't run. */}
      <div className="relative flex items-center justify-center gap-1.5 border-t border-surface-border dark:border-surface-dark-border px-4 py-2">
        <div className="inline-flex rounded-lg border border-surface-border dark:border-surface-dark-border p-0.5 bg-gray-50 dark:bg-gray-900">
          {LOG_MODES.map((m) => {
            const disabled = m.voice && !canConverse;
            const selected = mode === m.key;
            return (
              <button
                key={m.key}
                type="button"
                disabled={disabled}
                onClick={() => selectMode(m.key)}
                title={disabled ? 'Voice needs HTTPS or the app — not available here' : `Switch to ${m.label}`}
                className={`px-3 h-6 rounded-md text-sm font-medium transition-colors ${
                  selected
                    ? 'bg-primary-500 text-[#1a1200] shadow-sm'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800'
                } ${disabled ? 'opacity-40 cursor-not-allowed hover:bg-transparent dark:hover:bg-transparent' : ''}`}
              >
                {m.short}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => setShowInfo((v) => !v)}
          aria-label="What do the modes mean?"
          title="What do the modes mean?"
          className="flex h-6 w-6 items-center justify-center rounded-full border border-surface-border dark:border-surface-dark-border text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
        >
          i
        </button>
        {showInfo && (
          <div className="absolute right-4 bottom-full z-20 mb-1 w-80 max-w-[calc(100vw-2.5rem)] rounded-lg border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card p-4 shadow-lg text-sm text-gray-700 dark:text-gray-200 space-y-2.5">
            <p className="font-semibold text-gray-900 dark:text-white">Three ways to log</p>
            <p><span className="font-semibold text-gray-900 dark:text-white">Type</span> — write or dictate into the box and tap send. Best when you want to read the text before it goes.</p>
            <p><span className="font-semibold text-gray-900 dark:text-white">Auto</span> — fully hands-free. Tap Start, then just talk; it replies whenever you pause for a moment.</p>
            <p><span className="font-semibold text-gray-900 dark:text-white">Walkie-talkie</span> — hands-free, but you hold the floor. Talk as long as you like, then say your send word (“{getSendWord()}”) or tap Send. Interrupt its reply anytime. Tap <span className="font-medium">How to use</span> inside to hear it explained aloud.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationLogger;
